import React from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Layout } from '@/components/layout/Layout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorDisplay } from '@/components/common/ErrorDisplay';
import { Accordion } from '@/components/common/Accordion';
import { SentimentCard } from '@/components/analysis/SentimentCard';
import { EmotionCard } from '@/components/analysis/EmotionCard';
import { BehavioralTraitsCard } from '@/components/analysis/BehavioralTraitsCard';
import { ClarityAnalysis } from '@/components/analysis/ClarityAnalysis';
import { adminApi } from '@/services/api';
import type { Analysis } from '@/types/analysis';

const AdminAnalysisDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: fetchedData, isLoading, error } = useQuery({
    queryKey: ['admin', 'analysis', id],
    queryFn: async () => {
      const response = await adminApi.getAnalysis(id!);
      return response.data?.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });

  // Map fetched data to Analysis type (same logic as AnalysisDetail.tsx)
  const data: Analysis | null = fetchedData ? {
    _id: fetchedData._id,
    lite_doc_id: fetchedData._id,
    text: fetchedData.result?.input || fetchedData.text || '',
    context: fetchedData.context || '',
    date: fetchedData.date,
    user_id: fetchedData.user_id,
    org_id: fetchedData.org_id,
    metadata: fetchedData.metadata || undefined,
    sentiment: fetchedData.result?.analysis?.sentiment ? {
      label: fetchedData.result.analysis.sentiment.positivity > fetchedData.result.analysis.sentiment.negativity ? 'positive' :
             fetchedData.result.analysis.sentiment.negativity > fetchedData.result.analysis.sentiment.positivity ? 'negative' : 'neutral',
      score: Math.max(
        fetchedData.result.analysis.sentiment.positivity,
        fetchedData.result.analysis.sentiment.negativity,
        fetchedData.result.analysis.sentiment.neutral
      ),
    } : { label: 'neutral', score: 0 },
    emotions: fetchedData.result?.analysis?.emotion ? Object.entries(fetchedData.result.analysis.emotion)
      .map(([emotion, score]: [string, any]) => ({
        emotion: emotion.replace(/_rating$/, '').replace(/_/g, ' '),
        score: score,
      }))
      .filter((e: any) => e.score > 0.2)
      .sort((a: any, b: any) => b.score - a.score)
      .slice(0, 10) : [],
    traits: fetchedData.trait_analysis || {},
    clarity_analysis: fetchedData.clarity_analysis,
    ai_explanation: fetchedData.explanation,
  } : null;

  if (!id) {
    return <Navigate to="/admin/users" replace />;
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner size="lg" message="Loading analysis..." showTimeoutMessage={true} />
        </div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 flex items-center justify-center min-h-[60vh]">
          <ErrorDisplay
            type="not-found"
            title="Analysis Not Found"
            message="The analysis you're looking for doesn't exist."
            onGoBack={() => navigate('/admin/users')}
            showRetry={false}
            showGoBack={true}
          />
        </div>
      </Layout>
    );
  }

  const analysis = data as Analysis;
  const originalText = analysis.text || '';
  const analysisDate = new Date(analysis.date);

  // Build a back link to the user's history if we have user_id
  const userHistoryLink = analysis.user_id ? `/admin/users/${analysis.user_id}/history` : '/admin/users';

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Back Navigation */}
        <div className="mb-4">
          <Link
            to={userHistoryLink}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <span className="material-symbols-outlined text-xl mr-1">arrow_back</span>
            Back to History
          </Link>
        </div>

        {/* Admin Banner */}
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-amber-600">admin_panel_settings</span>
            <span className="text-amber-800 font-medium">Admin View</span>
          </div>
        </div>

        {/* Page Header */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="flex items-center justify-center gap-3 mb-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 px-4">
              Communication Analysis Results
            </h1>
            {analysis.metadata?.source === 'email' && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                <span className="material-symbols-outlined text-sm">mail</span>
                Email
              </span>
            )}
          </div>

          {analysis.metadata?.source === 'email' && analysis.metadata?.subject && (
            <p className="text-lg sm:text-xl font-medium text-gray-700 mb-2 px-4 italic">
              "{analysis.metadata.subject}"
            </p>
          )}

          <p className="text-sm sm:text-base md:text-lg text-gray-600 px-4">
            {analysis.metadata?.source === 'email' && analysis.metadata?.original_date ? (
              <>
                Email sent on {new Date(analysis.metadata.original_date).toLocaleDateString('en-US', {
                  year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
                })}
                <br />
                <span className="text-xs text-gray-500">
                  Analysed on {analysisDate.toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
                  })}
                </span>
              </>
            ) : (
              <>
                Analysis performed on {analysisDate.toLocaleDateString('en-US', {
                  year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
                })}
              </>
            )}
          </p>
        </div>

        {/* Original Message */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-2">Original Message</label>
          <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
            <pre className="whitespace-pre-wrap text-sm text-gray-900 font-sans">{originalText}</pre>
          </div>
        </div>

        {/* Analysis Sections */}
        <div className="space-y-6">
          <Accordion
            title="Response Analysis"
            defaultOpen={true}
            icon={<span className="material-symbols-outlined text-blue-500">analytics</span>}
          >
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-6">
              <SentimentCard sentiment={analysis.sentiment} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <EmotionCard emotions={analysis.emotions} />
              <BehavioralTraitsCard traits={analysis.traits} />
            </div>
          </Accordion>

          {analysis.clarity_analysis && (
            <Accordion
              title="Communication Clarity"
              icon={<span className="material-symbols-outlined text-purple-500">visibility</span>}
            >
              <ClarityAnalysis clarity={analysis.clarity_analysis} />
            </Accordion>
          )}

          {analysis.ai_explanation && (
            <Accordion
              title="AI Explanation"
              icon={<span className="material-symbols-outlined text-primary-500">psychology</span>}
            >
              <div className="prose prose-sm max-w-none [&_ul]:space-y-3 [&_ol]:space-y-3 [&_li]:leading-relaxed [&_p]:mb-4 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mt-6 [&_h2]:mb-4 [&_h2]:pb-2 [&_h2]:border-b [&_h2]:border-gray-200">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {analysis.ai_explanation}
                </ReactMarkdown>
              </div>
            </Accordion>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminAnalysisDetail;
